import { useState } from 'react'
import {
  ArrowPathIcon,
  CheckCircleIcon,
  DocumentArrowDownIcon,
} from '@heroicons/react/24/outline'

import { Badge, Button, Card, CardContent, Spinner } from '../../components/ui'
import { CsvTableViewer } from '../../components/tabular'
import { cn } from '../../shared/lib/cn'
import { useLagAccrualRun } from './api'
import { balanceLabel, formatCurrency } from './utils'

type ExportView = 'journal' | 'reversal'

export default function NetSuiteExportsPage() {
  const { data: run, isLoading, error } = useLagAccrualRun()
  const [view, setView] = useState<ExportView>('journal')

  if (isLoading) {
    return (
      <div className="flex min-h-[360px] items-center justify-center">
        <Spinner size="lg" />
      </div>
    )
  }

  if (error || !run) {
    return (
      <Card>
        <CardContent className="p-6 text-sm text-red-600 dark:text-red-400">
          Unable to load NetSuite exports.
        </CardContent>
      </Card>
    )
  }

  const rows = view === 'journal' ? run.journal_lines : run.reversal_lines
  const balance = view === 'journal' ? run.journal_balance : run.reversal_balance
  const filename = view === 'journal' ? 'EC02d_JE_Load_Apr2025.csv' : 'EC02d_Reversal_Load_May2025.csv'

  return (
    <div className="grid gap-6">
      <section className="grid gap-4 lg:grid-cols-[1fr_1fr_1.2fr]">
        <Card>
          <CardContent className="p-5">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  JE debit
                </p>
                <p className="mt-2 text-2xl font-semibold text-charcoal dark:text-gray-100">
                  {formatCurrency(run.journal_balance.debit_total)}
                </p>
              </div>
              <DocumentArrowDownIcon className="h-5 w-5 text-gray-400" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  Reversal credit
                </p>
                <p className="mt-2 text-2xl font-semibold text-charcoal dark:text-gray-100">
                  {formatCurrency(run.reversal_balance.credit_total)}
                </p>
              </div>
              <ArrowPathIcon className="h-5 w-5 text-gray-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-emerald-200 bg-emerald-50 dark:border-emerald-900/50 dark:bg-emerald-950/10">
          <CardContent className="flex h-full items-start gap-3 p-5">
            <CheckCircleIcon className="mt-0.5 h-5 w-5 text-emerald-600 dark:text-emerald-400" />
            <div>
              <p className="font-medium text-emerald-950 dark:text-emerald-100">
                Both load files balance
              </p>
              <p className="mt-1 text-sm leading-6 text-emerald-800 dark:text-emerald-200">
                Total debits and credits are validated before the controller uploads to NetSuite.
              </p>
            </div>
          </CardContent>
        </Card>
      </section>

      <CsvTableViewer
        title={view === 'journal' ? 'Journal Entry Load File' : 'Auto-Reversal Load File'}
        description="Search, sort, paginate, and export the NetSuite-ready CSV rows."
        rows={rows}
        columns={[
          'Journal Number',
          'Journal Date',
          'Posting Period',
          'Memo',
          'Account Number',
          'Account Name',
          'Debit',
          'Credit',
          'Department',
          'Class',
          'Location',
          'Source',
          'Auto-Reverse',
        ]}
        filename={filename}
        initialPageSize={25}
        maxHeight="640px"
        toolbar={
          <>
            <Button
              type="button"
              variant={view === 'journal' ? 'brand' : 'ghost'}
              size="sm"
              onClick={() => setView('journal')}
            >
              Journal Entry
            </Button>
            <Button
              type="button"
              variant={view === 'reversal' ? 'brand' : 'ghost'}
              size="sm"
              onClick={() => setView('reversal')}
            >
              Auto-Reversal
            </Button>
            <Badge
              className={cn(
                balance.is_balanced
                  ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300'
                  : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
              )}
            >
              {balanceLabel(balance)}
            </Badge>
          </>
        }
      />
    </div>
  )
}
